import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Stack,
  Alert,
  Snackbar,
} from '@mui/material';
import Load from '../Load';
import {
  LIGA_CONTA_IUGU_POR_LIGA_ID,
  LigaContaIuguPorLigaIdData,
  LigaContaIuguPorLigaIdVars,
  LigaContaIuguInput,
  atualizarLigaContaIugu,
  criarLigaContaIugu,
} from '../../services/ligaContaIuguService';

interface ConfigPagamentosConfiguracaoTabProps {
  ligaId: string;
}

// Form schema
const formSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  accountId: z.string().optional(),
  verificada: z.boolean().optional(),
  ativa: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const ConfigPagamentosConfiguracaoTab: React.FC<ConfigPagamentosConfiguracaoTabProps> = ({ ligaId }) => {
  const { data, loading, error } = useQuery<LigaContaIuguPorLigaIdData, LigaContaIuguPorLigaIdVars>(
    LIGA_CONTA_IUGU_POR_LIGA_ID,
    {
      variables: { ligaId },
      skip: !ligaId,
    }
  );

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nome: '',
      accountId: '',
      verificada: false,
      ativa: false,
    },
  });

  const contaIugu = data?.ligaContaIuguPorLigaId;

  useEffect(() => {
    if (contaIugu) {
      reset({
        nome: contaIugu.nome || '',
        accountId: contaIugu.accountId || '',
        verificada: contaIugu.verificada || false,
        ativa: contaIugu.ativa || false,
      });
    }
  }, [contaIugu, reset]);

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const onSubmit = async (values: FormValues) => {
    try {
      const input: LigaContaIuguInput = {
        ligaId,
        nome: values.nome,
        accountId: values.accountId && values.accountId.trim() !== '' ? values.accountId : null,
        verificada: values.verificada,
        ativa: values.ativa,
      };

      if (contaIugu?.id) {
        // Update existing record
        await atualizarLigaContaIugu(contaIugu.id, input);
        setSnackbar({
          open: true,
          message: 'Configuração atualizada com sucesso.',
          severity: 'success',
        });
      } else {
        // Create new record
        await criarLigaContaIugu(input);
        setSnackbar({
          open: true,
          message: 'Configuração criada com sucesso.',
          severity: 'success',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: contaIugu?.id
          ? 'Erro ao atualizar configuração. Tente novamente.'
          : 'Erro ao criar configuração. Tente novamente.',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return <Load />;
  }

  if (error) {
    return <Alert severity="error">Erro ao carregar configuração. Tente novamente.</Alert>;
  }

  return (
    <>
      <Card variant="outlined">
        <CardHeader title="Configuração da Conta IUGU" />
        <CardContent>
          {!contaIugu && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Nenhuma configuração cadastrada. Preencha os campos abaixo e clique em Salvar para criar.
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <Controller
                name="nome"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome da Conta"
                    fullWidth
                    required
                    error={!!errors.nome}
                    helperText={errors.nome?.message as string}
                  />
                )}
              />

              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Account ID"
                    fullWidth
                    error={!!errors.accountId}
                    helperText={errors.accountId?.message as string}
                  />
                )}
              />

              <Stack direction="row" spacing={3}>
                <Controller
                  name="verificada"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={!!field.value} />}
                      label="Conta Verificada"
                    />
                  )}
                />

                <Controller
                  name="ativa"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={!!field.value} />}
                      label="Conta Ativa"
                    />
                  )}
                />
              </Stack>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ConfigPagamentosConfiguracaoTab;
